import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getMonthName, getCurrentYear, toISODateString } from '../lib/utils';
import { Plus, Pencil, Trash2, CalendarIcon, Wallet, Upload, FileSpreadsheet, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ExportButtons } from '../components/ExportButtons';
import { MissingDatesWarning } from '../components/MissingDatesWarning';
import { SnapshotButtons } from '../components/SnapshotButtons';

export function CAReelPage() {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [modesPaiement, setModesPaiement] = useState([]);
  const [year, setYear] = useState(getCurrentYear());
  const [month, setMonth] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterModePaiement, setFilterModePaiement] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);

  // Import Clyo state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importMode, setImportMode] = useState('fixed'); // 'fixed' or 'auto'
  const [importDate, setImportDate] = useState(toISODateString(new Date()));
  const [importDateOpen, setImportDateOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importHasDateCol, setImportHasDateCol] = useState(false);
  const [importing, setImporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { annee: year };
      if (month) params.mois = month;
      const res = await api.get('/journal-revenus', { params });
      setData(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  }, [api, year, month]);

  const fetchModes = useCallback(async () => {
    try {
      const res = await api.get('/listes/mode_paiement');
      const modes = res.data.map(m => m.valeur);
      setModesPaiement(modes.length > 0 ? modes : ['CB', 'Espèces', 'TR', 'Chèque']);
    } catch (error) {
      setModesPaiement(['CB', 'Espèces', 'TR', 'Chèque']);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchModes(); }, [fetchModes]);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());

  const handleDelete = async (id) => {
    try {
      await api.delete(`/journal-revenus/${id}`);
      toast.success('Supprimé');
      selectedIds.delete(id);
      setSelectedIds(new Set(selectedIds));
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      await api.post('/journal-revenus/delete-bulk', { ids: [...selectedIds] });
      toast.success(`${selectedIds.size} ligne(s) supprimée(s)`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map(d => d.id)));
    }
  };

  // Parse Clyo CSV client-side for preview
  const PLATEFORMES_EXCLUES = ['uber eats', 'deliveroo'];
  const COLONNES_IGNOREES = ['total', 'compte client', 'arrhes', 'avoir', 'fidélité', 'fidelite', 'employé', 'employe', 'dont règlement(s) client(s)', 'dont reglement(s) client(s)'];

  const parseClyo = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let text;
    try {
      const decoded = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
      text = decoded;
    } catch (e) {
      text = Array.from(bytes).map(b => String.fromCharCode(b)).join('');
    }
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
    if (lines.length < 2) return { rows: [], hasDateCol: false, isHorizontal: false };
    const sep = lines[0].includes(';') ? ';' : lines[0].includes('\t') ? '\t' : ',';
    const headersRaw = lines[0].split(sep).map(h => h.trim());
    const headersLower = headersRaw.map(h => h.toLowerCase());

    const isHorizontal = headersLower[0] === 'date' && headersRaw.length > 5;

    if (isHorizontal) {
      // Horizontal: Date;Especes;CB;...;Total;...
      const paymentCols = [];
      for (let i = 1; i < headersRaw.length; i++) {
        const hl = headersLower[i];
        if (COLONNES_IGNOREES.includes(hl)) continue;
        const isExclu = PLATEFORMES_EXCLUES.includes(hl);
        paymentCols.push({ idx: i, name: headersRaw[i], exclu: isExclu });
      }

      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const parts = lines[i].split(sep);
        const rawDate = parts[0]?.trim() || '';
        if (!rawDate || rawDate.toUpperCase() === 'TOTAL') continue;
        for (const col of paymentCols) {
          if (col.idx >= parts.length) continue;
          const mStr = (parts[col.idx] || '0').replace(/\xa0/g, '').replace(/\s/g, '').replace(',', '.');
          const montant = parseFloat(mStr) || 0;
          rows.push({ reglement: col.name, devises: 'Euros', montant, exclu: col.exclu, date: rawDate });
        }
      }
      return { rows, hasDateCol: true, isHorizontal: true };
    }

    // Vertical: Règlement;Devises;Montant (or Date;Règlement;...)
    const hasDateCol = headersLower.some(h => h === 'date' || h === 'dates');
    const dateIdx = headersLower.findIndex(h => h === 'date' || h === 'dates');
    let regIdx, montIdx;
    if (hasDateCol) {
      regIdx = headersLower.findIndex(h => ['règlement', 'reglement', 'mode', 'libellé', 'libelle'].includes(h));
      montIdx = headersLower.findIndex(h => ['montant', 'montant ttc'].includes(h));
      if (regIdx === -1) regIdx = dateIdx === 0 ? 1 : 0;
      if (montIdx === -1) montIdx = Math.max(regIdx + 1, 2);
    } else {
      regIdx = 0;
      montIdx = 2;
    }

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i].split(sep);
      if (parts.length < 3) continue;
      const reglement = parts[regIdx]?.trim() || '';
      const montant = parseFloat((parts[montIdx] || '0').replace(/\xa0/g, '').replace(/\s/g, '').replace(',', '.')) || 0;
      const isExclu = PLATEFORMES_EXCLUES.includes(reglement.toLowerCase());
      let rowDate = hasDateCol && dateIdx < parts.length ? parts[dateIdx]?.trim() || '' : '';
      rows.push({ reglement, devises: 'Euros', montant, exclu: isExclu, date: rowDate });
    }
    return { rows, hasDateCol, isHorizontal: false };
  };

  const handleImportFileSelect = (file) => {
    if (!file) return;
    setImportFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { rows, hasDateCol } = parseClyo(e.target.result);
        setImportPreview(rows);
        setImportHasDateCol(hasDateCol);
        if (hasDateCol) setImportMode('auto');
      } catch (err) {
        toast.error('Erreur lecture fichier');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImportClyo = async () => {
    if (!importFile) {
      toast.error('Sélectionnez un fichier');
      return;
    }
    if (importMode === 'fixed' && !importDate) {
      toast.error('Sélectionnez une date');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      let url = '/journal-revenus/import-clyo';
      if (importMode === 'fixed' && importDate) {
        url += `?date_import=${importDate}`;
      }
      const res = await api.post(url, formData);
      toast.success(res.data.message);
      setImportDialogOpen(false);
      setImportFile(null);
      setImportPreview(null);
      setImportHasDateCol(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  const uniqueModesPaiement = [...new Set(data.map(d => d.mode_paiement))].sort();

  const filteredData = data.filter(d => {
    if (dateFrom && d.date < dateFrom) return false;
    if (dateTo && d.date > dateTo) return false;
    if (filterModePaiement !== 'all' && d.mode_paiement !== filterModePaiement) return false;
    return true;
  });
  const totalTTC = filteredData.reduce((sum, r) => sum + r.montant_ttc, 0);
  const totalTVA = filteredData.reduce((sum, r) => sum + r.tva, 0);
  const previewTotal = importPreview ? importPreview.filter(r => !r.exclu).reduce((s, r) => s + r.montant, 0) : 0;
  const previewImportable = importPreview ? importPreview.filter(r => !r.exclu) : [];
  const previewExclues = importPreview ? importPreview.filter(r => r.exclu) : [];

  const RevenuForm = ({ item, onClose, defaultDate }) => {
    const [form, setForm] = useState(item ? {
      date: item.date,
      mode_paiement: item.mode_paiement,
      montant_ttc: item.montant_ttc,
      taux_tva: item.taux_tva
    } : {
      date: defaultDate || toISODateString(new Date()),
      mode_paiement: 'CB',
      montant_ttc: 0,
      taux_tva: 0.1
    });
    const [dateOpen, setDateOpen] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.id) {
          await api.put(`/journal-revenus/${item.id}`, form);
          toast.success('Modifié');
        } else {
          await api.post('/journal-revenus', form);
          toast.success('Créé');
        }
        fetchData();
        onClose();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur');
      }
    };

    const baseHT = form.montant_ttc / (1 + form.taux_tva);
    const tva = form.montant_ttc - baseHT;

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.date ? format(new Date(form.date), 'PPP', { locale: fr }) : 'Choisir'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={form.date ? new Date(form.date) : undefined}
                  onSelect={(date) => { setForm({ ...form, date: toISODateString(date) }); setDateOpen(false); }}
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label>Mode de paiement</Label>
            <Select value={form.mode_paiement} onValueChange={(v) => setForm({ ...form, mode_paiement: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {modesPaiement.map(m => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Montant TTC</Label>
            <Input type="number" step="0.01" value={form.montant_ttc} onChange={(e) => setForm({ ...form, montant_ttc: parseFloat(e.target.value) || 0 })} />
          </div>
          <div className="space-y-2">
            <Label>Taux TVA</Label>
            <Select value={form.taux_tva.toString()} onValueChange={(v) => setForm({ ...form, taux_tva: parseFloat(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0.055">5.5%</SelectItem>
                <SelectItem value="0.1">10%</SelectItem>
                <SelectItem value="0.2">20%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
          <div>
            <span className="text-xs text-muted-foreground">Base HT</span>
            <p className="text-lg font-medium tabular-nums">{formatCurrency(baseHT)}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">TVA</span>
            <p className="text-lg font-medium tabular-nums">{formatCurrency(tva)}</p>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline">Annuler</Button></DialogClose>
          <Button type="submit">{item?.id ? 'Modifier' : 'Créer'}</Button>
        </DialogFooter>
      </form>
    );
  };

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title" data-testid="ca-reel-title">CA Réel (Encaissements)</h1>
          <p className="text-muted-foreground mt-1">Journal des revenus par mode de paiement</p>
        </div>
        <div className="flex gap-2">
          {/* Import Clyo Button */}
          <div className="flex gap-2 flex-wrap">
            <SnapshotButtons module="ca_reel" moduleLabel="CA Réel" />
            <ExportButtons api={api} moduleKey="ca_reel" year={year} month={month} />
          </div>
          <Button variant="outline" className="gap-2" data-testid="import-clyo-btn" onClick={() => setImportDialogOpen(true)}>
            <Upload className="w-4 h-4" /> Import Clyo
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-revenu-btn" onClick={() => setEditItem(null)}>
                <Plus className="w-4 h-4" /> Nouvel encaissement
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editItem ? 'Modifier' : 'Nouvel'} Encaissement</DialogTitle>
              </DialogHeader>
              <RevenuForm item={editItem} onClose={() => { setDialogOpen(false); setPrefillDate(null); }} defaultDate={prefillDate} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-[120px]" data-testid="filter-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={month?.toString() || 'all'} onValueChange={(v) => setMonth(v === 'all' ? null : parseInt(v))}>
          <SelectTrigger className="w-[160px]" data-testid="filter-month"><SelectValue placeholder="Tous les mois" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterModePaiement} onValueChange={setFilterModePaiement}>
          <SelectTrigger className="w-[180px]" data-testid="filter-mode-paiement">
            <SelectValue placeholder="Mode de paiement" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les modes</SelectItem>
            {uniqueModesPaiement.map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Du</Label>
          <Input type="date" className="w-[150px] h-9" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          <Label className="text-xs whitespace-nowrap">Au</Label>
          <Input type="date" className="w-[150px] h-9" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
          {(dateFrom || dateTo || filterModePaiement !== 'all') && <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setFilterModePaiement('all'); }}>Effacer</Button>}
        </div>
        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
            <Wallet className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Total TTC:</span>
            <span className="text-lg font-bold text-primary tabular-nums">{formatCurrency(totalTTC)}</span>
          </div>
          <div className="px-3 py-2 bg-muted rounded-full text-sm">
            TVA: <span className="font-medium tabular-nums">{formatCurrency(totalTVA)}</span>
          </div>
        </div>
      </div>

      {/* Bulk delete bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg" data-testid="bulk-delete-bar">
          <span className="text-sm font-medium">{selectedIds.size} ligne(s) sélectionnée(s)</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="deselect-all-btn">
            Désélectionner
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" className="gap-1" data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4" /> Supprimer la sélection
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer {selectedIds.size} encaissement(s) ?</AlertDialogTitle>
                <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteSelected}>Supprimer</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Missing dates warning */}
      <MissingDatesWarning data={filteredData} dateField="date" year={year} month={month ? parseInt(month) : null} onDateClick={(date) => { setPrefillDate(date); setEditItem(null); setDialogOpen(true); }} />

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                    checked={filteredData.length > 0 && selectedIds.size === filteredData.length}
                    onChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Mode de paiement</TableHead>
                <TableHead className="text-right">Montant TTC</TableHead>
                <TableHead className="text-right">Taux TVA</TableHead>
                <TableHead className="text-right">Base HT</TableHead>
                <TableHead className="text-right">TVA</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Aucun encaissement pour cette période
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map(item => (
                  <TableRow key={item.id} className={selectedIds.has(item.id) ? 'bg-primary/5' : ''}>
                    <TableCell>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        data-testid={`select-row-${item.id}`}
                      />
                    </TableCell>
                    <TableCell>{formatDate(item.date)}</TableCell>
                    <TableCell className="font-medium">{item.mode_paiement}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatCurrency(item.montant_ttc)}</TableCell>
                    <TableCell className="text-right tabular-nums">{(item.taux_tva * 100).toFixed(1)}%</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(item.base_ht)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(item.tva)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setDialogOpen(true); }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cet encaissement ?</AlertDialogTitle>
                              <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(item.id)}>Supprimer</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Import Clyo Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open);
        if (!open) { setImportFile(null); setImportPreview(null); setImportHasDateCol(false); setImportMode('fixed'); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import Fichier Clyo
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Mode selector */}
            <div className="space-y-2">
              <Label>Mode d'import</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={importMode === 'fixed' ? 'default' : 'outline'}
                  className="h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => setImportMode('fixed')}
                  data-testid="mode-fixed-btn"
                >
                  <CalendarIcon className="w-4 h-4" />
                  <span className="text-xs">Date fixe</span>
                </Button>
                <Button
                  type="button"
                  variant={importMode === 'auto' ? 'default' : 'outline'}
                  className="h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => setImportMode('auto')}
                  data-testid="mode-auto-btn"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span className="text-xs">Dates dans le fichier</span>
                </Button>
              </div>
            </div>

            {/* Date picker (only in fixed mode) */}
            {importMode === 'fixed' && (
              <div className="space-y-2">
                <Label>Date de la journée</Label>
                <Popover open={importDateOpen} onOpenChange={setImportDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="import-date-picker">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {importDate ? format(new Date(importDate), 'EEEE d MMMM yyyy', { locale: fr }) : 'Choisir la date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={importDate ? new Date(importDate) : undefined}
                      onSelect={(date) => { setImportDate(toISODateString(date)); setImportDateOpen(false); }}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {importMode === 'auto' && (
              <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                Le fichier doit contenir une colonne <strong>Date</strong> (format JJ/MM/AAAA). Chaque ligne sera importée avec sa propre date.
              </p>
            )}

            {/* File upload */}
            <div className="space-y-2">
              <Label>Fichier CSV</Label>
              <input
                type="file"
                accept=".csv,.txt"
                className="hidden"
                id="clyo-file"
                data-testid="clyo-file-input"
                onChange={(e) => { if (e.target.files[0]) handleImportFileSelect(e.target.files[0]); }}
              />
              <Button
                variant="outline"
                className="w-full h-16 border-dashed"
                data-testid="clyo-file-btn"
                onClick={() => document.getElementById('clyo-file').click()}
              >
                {importFile ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span>{importFile.name}</span>
                    {importHasDateCol && <Badge variant="secondary" className="text-xs">Dates détectées</Badge>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Upload className="w-5 h-5" />
                    <span>Sélectionner le fichier</span>
                  </div>
                )}
              </Button>
            </div>

            {/* Preview */}
            {importPreview && importPreview.length > 0 && (
              <div className="space-y-3">
                <Separator />
                <div className="text-sm font-medium">
                  Aperçu — {previewImportable.length} ligne(s) à importer, Total: {formatCurrency(previewTotal)}
                </div>
                <div className="border rounded-lg overflow-hidden max-h-[250px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {(importMode === 'auto' && importHasDateCol) && <TableHead>Date</TableHead>}
                        <TableHead>Règlement</TableHead>
                        <TableHead className="text-right">Montant TTC</TableHead>
                        <TableHead className="text-center w-[90px]">Statut</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreview.map((row, i) => {
                        const isSkipped = row.exclu;
                        return (
                          <TableRow key={i} className={isSkipped ? 'opacity-40 line-through' : ''}>
                            {(importMode === 'auto' && importHasDateCol) && (
                              <TableCell className="text-sm">{row.date}</TableCell>
                            )}
                            <TableCell className="font-medium">{row.reglement}</TableCell>
                            <TableCell className={`text-right tabular-nums font-medium ${!isSkipped ? (row.montant > 0 ? 'text-green-600' : 'text-gray-500') : ''}`}>
                              {formatCurrency(row.montant)}
                            </TableCell>
                            <TableCell className="text-center">
                              {row.exclu ? (
                                <Badge variant="outline" className="text-xs">Plateforme</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs">Import</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="bg-muted/50 font-bold">
                        {(importMode === 'auto' && importHasDateCol) && <TableCell></TableCell>}
                        <TableCell>Total CA Réel</TableCell>
                        <TableCell className="text-right tabular-nums text-green-700">{formatCurrency(previewTotal)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                {previewExclues.length > 0 && (
                  <p className="text-xs text-amber-600">
                    {previewExclues.map(r => r.reglement).join(', ')} exclu(s) du CA Réel (traité dans Plateformes).
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Lignes à 0 ignorées. TVA 10% (restauration).
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Annuler</Button>
            <Button
              onClick={handleImportClyo}
              disabled={!importFile || (importMode === 'fixed' && !importDate) || importing}
              data-testid="confirm-clyo-import-btn"
            >
              {importing ? 'Import en cours...' : (
                importMode === 'fixed'
                  ? `Importer pour le ${importDate ? format(new Date(importDate), 'dd/MM/yyyy') : '...'}`
                  : 'Importer avec dates du fichier'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
